import { roundTo } from "./tillHelpers";

/**
 * Data Integrity assertion utilities for POS order processing.
 *
 * These functions validate cart/order consistency before persisting or syncing.
 * They LOG warnings for minor mismatches (< 1.00) and BLOCK for critical ones (> 1.00).
 */

const TOLERANCE = 0.01;
const BLOCK_THRESHOLD = 1.00;

/**
 * DI-01: Validate cart.total matches sum(items.nettotal) + tax + charges.
 *
 * @param {Object} cart - Cart object with items, total, tax, and charge fields
 * @param {string} [context=''] - Caller context for log messages
 * @returns {{ valid: boolean, diff: number, message?: string }}
 */
export function assertOrderTotals(cart, context = "") {
  const lineItemSum = (cart.items || []).reduce(
    (s, i) => s + (Number(i.nettotal) || 0),
    0
  );
  // Cart charge fields — note the typo "deiveryCharge" in the cart schema
  const charges =
    (Number(cart.serviceCharge) || 0) +
    (Number(cart.tip) || 0) +
    (Number(cart.deiveryCharge) || 0) +
    (Number(cart.packingCharge) || 0) +
    (Number(cart.others) || 0) +
    (Number(cart.tcs) || 0);
  // nettotal already includes tax (both tax-inclusive and tax-exclusive modes),
  // so do NOT add cart.tax again — that would double-count.
  // roundOff = gross - rounded; some paths set total=rounded, subtract roundOff to match.
  const roundOff = Number(cart.roundOff) || 0;
  const expectedTotal = lineItemSum + charges - roundOff;
  const cartTotal = Number(cart.total) || 0;
  const diff = Math.abs(cartTotal - expectedTotal);

  if (diff > BLOCK_THRESHOLD) {
    logIntegrityViolation("DI-01", {
      context,
      cartTotal,
      expectedTotal,
      diff: roundTo(diff, 4),
      lineItemSum: roundTo(lineItemSum, 4),
      tax: cart.tax,
      charges: roundTo(charges, 4),
    });
    return {
      valid: false,
      diff: roundTo(diff, 4),
      message: `Order total mismatch: cart.total=${cartTotal}, expected=${roundTo(expectedTotal, 4)}, diff=${roundTo(diff, 4)}`,
    };
  }
  if (diff > TOLERANCE) {
    logIntegrityViolation("DI-01-warn", {
      context,
      cartTotal,
      expectedTotal: roundTo(expectedTotal, 4),
      diff: roundTo(diff, 4),
    });
  }
  return { valid: true, diff: roundTo(diff, 4) };
}

/**
 * DI-02: Validate sum(cart.payments) matches cart.total.
 *
 * For return orders (cart.isReturn), payments may still be positive at
 * validation time (negated later in processOrderApproved), so we compare
 * absolute values. For exchange orders, caller should pass the activeCart.
 *
 * @param {Object} cart - Cart object with payments array and total
 * @param {string} [context=''] - Caller context for log messages
 * @returns {{ valid: boolean, diff: number, message?: string }}
 */
export function assertPaymentBalance(cart, context = "") {
  const paymentSum = (cart.payments || []).reduce(
    (s, p) => s + (Number(p.amount) || 0),
    0
  );
  const isReturn = cart.isReturn === true || cart.isReturn === "Y";
  const cartTotal = Number(cart.total) || 0;
  // For returns, payments may not yet be negated at validation time —
  // compare absolute values so sign convention doesn't cause false failures
  const diff = isReturn
    ? Math.abs(Math.abs(paymentSum) - Math.abs(cartTotal))
    : Math.abs(paymentSum - cartTotal);

  if (diff > BLOCK_THRESHOLD) {
    logIntegrityViolation("DI-02", {
      context,
      paymentSum: roundTo(paymentSum, 4),
      cartTotal: roundTo(cartTotal, 4),
      diff: roundTo(diff, 4),
      paymentCount: cart.payments?.length || 0,
      isReturn,
    });
    return {
      valid: false,
      diff: roundTo(diff, 4),
      message: `Payment total mismatch: payments=${roundTo(paymentSum, 4)}, cartTotal=${roundTo(cartTotal, 4)}, diff=${roundTo(diff, 4)}`,
    };
  }
  if (diff > TOLERANCE) {
    logIntegrityViolation("DI-02-warn", {
      context,
      paymentSum: roundTo(paymentSum, 4),
      cartTotal: roundTo(cartTotal, 4),
      diff: roundTo(diff, 4),
    });
  }
  return { valid: true, diff: roundTo(diff, 4) };
}

/**
 * DI-03: Validate order JSON self-consistency: nettotal + taxamt + tcs === orderTotal.
 *
 * If mismatch is detected, returns a corrected nettotal derived from orderTotal.
 *
 * @param {Object} payload - Object with nettotal, taxamt, tcs (or charges), and orderTotal
 * @param {string} [context=''] - Caller context
 * @returns {{ valid: boolean, diff: number, correctedNettotal?: number }}
 */
export function assertOrderJSON(payload, context = "") {
  const nettotal = Number(payload.nettotal) || 0;
  const taxamt = Number(payload.taxamt) || 0;
  const tcs = Number(payload.tcs) || 0;
  const orderTotal = Number(payload.orderTotal) || 0;
  const reconstructed = nettotal + taxamt + tcs;
  const diff = Math.abs(reconstructed - orderTotal);

  if (diff > TOLERANCE) {
    const correctedNettotal = orderTotal - taxamt - tcs;
    logIntegrityViolation("DI-03", {
      context,
      originalNettotal: roundTo(nettotal, 4),
      correctedNettotal: roundTo(correctedNettotal, 4),
      taxamt: roundTo(taxamt, 4),
      tcs: roundTo(tcs, 4),
      orderTotal: roundTo(orderTotal, 4),
      reconstructed: roundTo(reconstructed, 4),
      diff: roundTo(diff, 4),
    });
    return {
      valid: false,
      diff: roundTo(diff, 4),
      correctedNettotal: roundTo(correctedNettotal, 4),
    };
  }
  return { valid: true, diff: roundTo(diff, 4) };
}

/**
 * DI-07: Log adjustments made by adjustOrderTotals.
 *
 * Should be called BEFORE any silent adjustment is applied.
 *
 * @param {Object} original - { total, tax } before adjustment
 * @param {Object} adjusted - { total, tax } after adjustment
 */
export function logAdjustOrderTotals(original, adjusted) {
  const grossDiff = adjusted.total - original.total;
  const taxDiff = adjusted.tax - original.tax;
  if (Math.abs(grossDiff) > TOLERANCE || Math.abs(taxDiff) > TOLERANCE) {
    logIntegrityViolation("DI-07", {
      originalTotal: original.total,
      adjustedTotal: adjusted.total,
      grossDiff: roundTo(grossDiff, 4),
      originalTax: original.tax,
      adjustedTax: adjusted.tax,
      taxDiff: roundTo(taxDiff, 4),
    });
  }
}

/**
 * DI-15 / DI-19: Verify sync completeness after processSync.
 *
 * Checks that critical tables (products, productCategories, pricingRules) have
 * at least one record. Returns a summary object with counts and a warning flag.
 *
 * @param {Object} dbAdapter - Database adapter (Dexie or SQLite)
 * @returns {Promise<{ complete: boolean, counts: Object, warnings: string[] }>}
 */
export async function verifySyncCompleteness(dbAdapter) {
  const warnings = [];
  const counts = {};
  try {
    const products = await dbAdapter.products.count();
    const categories = await dbAdapter.productCategories.count();
    const pricingRules = await dbAdapter.pricingRules.count();
    const posSaletypes = await dbAdapter.posSaletypes.count();

    counts.products = products;
    counts.productCategories = categories;
    counts.pricingRules = pricingRules;
    counts.posSaletypes = posSaletypes;

    if (products === 0) {
      warnings.push("No products synced — POS cannot function without products");
    }
    if (categories === 0) {
      warnings.push("No product categories synced");
    }
    if (pricingRules === 0) {
      // pricingRules can legitimately be 0 for some tenants — warn but don't fail
      warnings.push("No pricing rules synced (may be expected for this tenant)");
    }
    if (posSaletypes === 0) {
      warnings.push("No POS sale types synced — sale type selection will fail");
    }
  } catch (err) {
    warnings.push(`Sync verification DB error: ${err?.message || err}`);
  }

  const complete = counts.products > 0 && counts.posSaletypes > 0;
  if (warnings.length > 0) {
    logIntegrityViolation("DI-19", { counts, warnings });
  }
  return { complete, counts, warnings };
}

/**
 * DI-28: Log gift card deduction for tracking/reconciliation.
 *
 * Called when a gift card redemption API succeeds BEFORE order confirmation.
 * This creates an audit trail so that if the order subsequently fails, the
 * deduction can be identified and manually reconciled.
 *
 * @param {Object} params
 * @param {string} params.cardNo - Gift card number
 * @param {number} params.amount - Amount deducted
 * @param {string} params.referenceId - Transaction reference ID
 * @param {string} params.sOrderID - Order ID (if available)
 * @param {string} params.tillId - Till ID
 */
export function logGiftCardDeduction({ cardNo, amount, referenceId, sOrderID, tillId }) {
  console.warn(
    `[DI-28] Gift card deducted pre-confirmation: card=${cardNo}, amount=${amount}, ref=${referenceId}, order=${sOrderID || "pending"}, till=${tillId || "unknown"}`
  );
  logIntegrityViolation("DI-28", {
    type: "gift_card_pre_deduction",
    cardNo,
    amount,
    referenceId,
    sOrderID: sOrderID || null,
    tillId: tillId || null,
    timestamp: new Date().toISOString(),
  });
}

/**
 * DI-29: Log loyalty points deduction for tracking/reconciliation.
 *
 * Same rationale as DI-28 — loyalty points are deducted before order
 * confirmation. This creates an audit trail for reconciliation.
 *
 * @param {Object} params
 * @param {string} params.customerId - Customer ID
 * @param {number} params.points - Points/amount being redeemed
 * @param {string} params.sOrderID - Order ID (if available)
 * @param {string} params.tillId - Till ID
 * @param {string} params.paymentMethodName - Payment method name
 */
export function logLoyaltyDeduction({ customerId, points, sOrderID, tillId, paymentMethodName }) {
  console.warn(
    `[DI-29] Loyalty points used pre-confirmation: customer=${customerId}, points=${points}, order=${sOrderID || "pending"}, till=${tillId || "unknown"}`
  );
  logIntegrityViolation("DI-29", {
    type: "loyalty_pre_deduction",
    customerId,
    points,
    sOrderID: sOrderID || null,
    tillId: tillId || null,
    paymentMethodName: paymentMethodName || null,
    timestamp: new Date().toISOString(),
  });
}

/**
 * DI-30: Log coupon validation with terminal context.
 *
 * Creates an audit trail showing which terminal validated a coupon and when,
 * to aid in diagnosing simultaneous-use race conditions.
 *
 * @param {Object} params
 * @param {string} params.couponCode - The coupon code being validated
 * @param {string} params.referenceId - Unique reference for this validation
 * @param {string} params.tillId - Till/terminal ID
 * @param {string} params.bunitId - Business unit ID
 */
export function logCouponValidation({ couponCode, referenceId, tillId, bunitId }) {
  logIntegrityViolation("DI-30", {
    type: "coupon_validation",
    couponCode,
    referenceId,
    tillId: tillId || null,
    bunitId: bunitId || null,
    timestamp: new Date().toISOString(),
  });
}

/**
 * DI-S1: Re-verify stock at checkout for all cart items.
 *
 * Performs a non-blocking stock check. Returns { allAvailable, warnings[] }.
 * Only checks items with type "I" or no type. Skips in offline mode.
 *
 * @param {Object} params
 * @param {Object} params.cart - Cart object
 * @param {Function} params.verifyStockService - The verifyStock API service
 * @param {Object} params.tillData - Till configuration
 * @returns {Promise<{ allAvailable: boolean, warnings: string[] }>}
 */
export async function reVerifyStockAtCheckout({ cart, verifyStockService, tillData }) {
  const warnings = [];
  if (!navigator.onLine) {
    return { allAvailable: true, warnings: ["Offline mode — stock re-check skipped"] };
  }

  const stockItems = (cart.items || []).filter(
    (item) => (item.type === "I" || !item.type) && !item.isGiftCard && !item.isReturn
  );

  if (stockItems.length === 0) {
    return { allAvailable: true, warnings: [] };
  }

  try {
    const buildCartProducts = stockItems
      .map(({ value, weight, mBatchId, csBunitId }) =>
        `{ productId: "${value}", mBatchId: ${mBatchId ? `"${mBatchId}"` : null}, orderQty: ${weight}, cSBunitID: ${csBunitId ? `"${csBunitId}"` : null} }`
      )
      .join(", ");

    const data = {
      query: `query {
        verifyStock(order: {
          orderNo: "${cart.documentno || "CHECKOUT_VERIFY"}"
          tillId: "${tillData?.tillAccess?.cwrTill?.searchKey || ""}"
          cartItems: [${buildCartProducts}]
        }) {
          cartItems {
            productId
            orderQty
            availabilityStatus
            availableQty
          }
        }
      }`,
    };

    const response = await verifyStockService(data);
    if (response?.status === 200) {
      const resultItems = response.data?.data?.verifyStock?.cartItems || [];
      for (const resItem of resultItems) {
        if (resItem.availabilityStatus !== "Yes") {
          const cartItem = stockItems.find((i) => i.value === resItem.productId);
          const name = cartItem?.name || resItem.productId;
          if (resItem.availableQty < (cartItem?.weight || 0)) {
            warnings.push(
              `${name}: requested ${cartItem?.weight}, available ${resItem.availableQty}`
            );
          }
        }
      }
    }
  } catch (err) {
    console.warn("[DI-S1] Stock re-verification failed (non-blocking):", err?.message);
    // Non-blocking — don't fail the checkout
  }

  if (warnings.length > 0) {
    logIntegrityViolation("DI-S1", {
      type: "checkout_stock_warning",
      warnings,
      timestamp: new Date().toISOString(),
    });
  }

  return { allAvailable: warnings.length === 0, warnings };
}

/**
 * DI-31: Check documentno uniqueness before saving order.
 *
 * Soft check — NEVER blocks the order. If a collision is found, auto-bumps
 * the document sequence and returns a corrected documentno. The caller
 * should use the returned documentno on the cart before persisting.
 *
 * @param {Object} params
 * @param {Object} params.dbAdapter - Database adapter (db.orders must support .where)
 * @param {string} params.documentno - The documentno about to be saved
 * @param {string} params.sOrderID - Current order's sOrderID (to exclude self-updates)
 * @param {string} params.prefix - Till prefix for regenerating documentno
 * @param {Object} params.sessionStore - useSessionStore (for reading/bumping sequence)
 * @returns {Promise<{ unique: boolean, documentno: string, recovered?: boolean }>}
 */
export async function assertDocumentNoUnique({ dbAdapter, documentno, sOrderID, prefix, sessionStore, sequenceType = "regular" }) {
  try {
    if (!documentno) return { unique: true, documentno };

    const existing = await dbAdapter.orders.where("documentno").equals(documentno).toArray();
    const collision = existing.find((o) => o.sOrderID !== sOrderID);

    if (!collision) return { unique: true, documentno };

    // Collision found — auto-recover by finding max sequence and bumping past it
    const allOrders = await dbAdapter.orders.toArray();
    let maxSeq = 0;
    for (const order of allOrders) {
      if (order.documentno && order.documentno.startsWith(prefix)) {
        const seqPart = parseInt(order.documentno.slice(prefix.length), 10);
        if (!isNaN(seqPart) && seqPart > maxSeq) maxSeq = seqPart;
      }
    }
    const recoveredSeq = maxSeq + 1;
    let suffix = "";
    if (sequenceType === "preorder") {
      suffix = sessionStore?.getState?.()?.preorderSequence?.suffix || "";
    } else if (sequenceType === "cancelled") {
      suffix = sessionStore?.getState?.()?.cancelledOrderSequence?.suffix || "";
    }
    const recoveredDocNo = `${prefix}${recoveredSeq}${suffix}`;

    if (sessionStore?.getState) {
      if (sequenceType === "preorder") {
        const current = sessionStore.getState().preorderSequence;
        if (current) sessionStore.getState().setPreorderSequence({ ...current, nextNo: recoveredSeq });
      } else if (sequenceType === "cancelled") {
        const current = sessionStore.getState().cancelledOrderSequence;
        if (current) sessionStore.getState().setCancelledOrderSequence({ ...current, nextNo: recoveredSeq });
      } else {
        sessionStore.getState().setDocumentSequence(recoveredSeq);
      }
    }

    logIntegrityViolation("DI-31", {
      type: "duplicate_documentno_recovered",
      originalDocNo: documentno,
      recoveredDocNo,
      collidedWithSOrderID: collision.sOrderID,
      recoveredSeq,
      timestamp: new Date().toISOString(),
    });

    return { unique: false, documentno: recoveredDocNo, recovered: true };
  } catch (err) {
    // NEVER block — if the check itself fails, let the order through with original documentno
    console.warn("[DI-31] Document number uniqueness check failed (non-blocking):", err?.message);
    return { unique: true, documentno };
  }
}

/**
 * DI-32: Recover orders stuck in isSynced=3 (syncing) after crash/reload.
 *
 * On app start, orders left in isSynced=3 from a previous session are stuck
 * forever — the sync interval only picks up 0 and 2. This resets stale ones
 * back to 0 so they get retried.
 *
 * @param {Object} dbAdapter - Database adapter (db.orders)
 * @param {number} [staleMinutes=2] - Minutes after which isSynced=3 is considered stale
 * @returns {Promise<{ recovered: number, orders: string[] }>}
 */
export async function recoverStuckSyncOrders(dbAdapter, staleMinutes = 2) {
  try {
    const stuckOrders = await dbAdapter.orders.where("isSynced").equals(3).toArray();
    if (stuckOrders.length === 0) return { recovered: 0, orders: [] };

    const now = Date.now();
    const staleThreshold = staleMinutes * 60 * 1000;
    const recovered = [];

    for (const order of stuckOrders) {
      const lastAttempt = order.lastSyncAttempt ? new Date(order.lastSyncAttempt).getTime() : 0;
      const age = now - lastAttempt;

      if (age > staleThreshold || !order.lastSyncAttempt) {
        await dbAdapter.orders.where("sOrderID").equals(order.sOrderID).modify({ isSynced: 0 });
        recovered.push(order.sOrderID);
      }
    }

    if (recovered.length > 0) {
      logIntegrityViolation("DI-32", {
        type: "stuck_sync_recovery",
        recoveredCount: recovered.length,
        sOrderIDs: recovered,
        staleMinutes,
        timestamp: new Date().toISOString(),
      });
    }

    return { recovered: recovered.length, orders: recovered };
  } catch (err) {
    console.warn("[DI-32] Stuck sync recovery failed (non-blocking):", err?.message);
    return { recovered: 0, orders: [] };
  }
}

/**
 * Central logging function for all data integrity violations.
 *
 * @param {string} checkId - e.g. "DI-01", "DI-02-warn"
 * @param {Object} details - Structured details of the violation
 */
export function logIntegrityViolation(checkId, details) {
  console.error(
    `[DATA-INTEGRITY] ${checkId}:`,
    JSON.stringify(details)
  );
}
